import {
    Button,
    Checkbox,
    Input,
    CheckboxOnChangeData,
    Field
} from "@fluentui/react-components";
import { AddRegular, EditRegular, DeleteRegular, CheckmarkRegular, DismissRegular, GlobeLocationRegular} from "@fluentui/react-icons";
import Paginator from "#root/components/Paginator";
import { useLocations, useUpdateLocation, useDeleteLocation, useCreateLocation, Location } from "#root/queries/locations";
import { useState } from "react";
import "./styles.scss";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { showToast } from "#root/components/GlobalToaster";
import { DialogBox } from "#root/components/DialogBox";
import { Breadcrumbs } from "#root/components/Breadcrumbs";

const Locations: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [editingLocation, setEditingLocation] = useState<Location | null>(null);
    const [editedValues, setEditedValues] = useState<Location>({});
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [locationsToDelete, setLocationsToDelete] = useState<Location[]>([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newLocation, setNewLocation] = useState({ name: '', address: '' });

    const {
        data: locationsData,
        isLoading,
        refetch
    } = useLocations({
        take: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage
    });

    const updateLocationMutation = useUpdateLocation();
    const deleteLocationMutation = useDeleteLocation();
    const createLocationMutation = useCreateLocation();

    const totalCount = locationsData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const locations = locationsData?.items ?? [];

    // Pagination handling

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        setSelectedLocations([]);
    };

    // Select handling

    const getSelectionState = () => {
        if (selectedLocations.length === 0) return false;
        if (selectedLocations.length === locations.length) return true;
        return 'mixed';
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedLocations(locations.map(location => location.id!));
        } else {
            setSelectedLocations([]);
        }
    };

    const handleSelect = (id: string, checked: boolean) => {
        setSelectedLocations(prev =>
            checked ? [...prev, id] : prev.filter(locId => locId !== id)
        );
    };

    // Edit handling
    function handleEdit(location: Location) {
        setEditingLocation(location);
        setEditedValues(location);
    };

    async function handleEditConfirm() {
        if (editingLocation?.id && editedValues) {
            try {
                if (editedValues !== editingLocation) {
                    // Check if address has changed
                    const hasAddressChanged = editedValues.address !== editingLocation.address;
                    
                    // Create the base location object
                    const locationToUpdate: Location = {
                        id: editingLocation.id,
                        ...editedValues
                    };
                    
                    // If address changed, remove coordinates to trigger geocoding on the backend
                    if (hasAddressChanged) {
                        // Delete the properties instead of setting to null to match the Location interface
                        delete locationToUpdate.latitude;
                        delete locationToUpdate.longitude;
                    }
                    
                    await updateLocationMutation.mutateAsync({
                        location: locationToUpdate
                    });
                    showToast('Location updated successfully', 'success');
                }
                setEditingLocation(null);
                setEditedValues({});
            } catch (error) {
                showToast('Failed to update location', 'error');
            }
        }
    };

    // Delete handling
    function handleDeleteClick(location: Location, e?: React.MouseEvent) {
        e?.stopPropagation();
        setLocationsToDelete([location]);
        setIsDeleteDialogOpen(true);
    };

    function handleDeleteMultipleClick() {
        const locationsToDelete = locations.filter(loc => loc.id && selectedLocations.includes(loc.id));
        setLocationsToDelete(locationsToDelete);
        setIsDeleteDialogOpen(true);
    };

    async function handleConfirmDelete() {
        try {
            await Promise.all(locationsToDelete.map(location => {
                return deleteLocationMutation.mutateAsync({
                    id: location.id!
                });
            }));
            setIsDeleteDialogOpen(false);
            showToast(`${locationsToDelete.length > 1 ? 'Locations' : 'Location'} deleted successfully`, 'success');
            setSelectedLocations([]);
            setLocationsToDelete([]);
            refetch();
        } catch (error) {
            showToast('Failed to delete location(s)', 'error');
        }
    };

    // Create handling
    async function handleConfirmCreate() {
        try {
            await createLocationMutation.mutateAsync({
                location: {
                    name: newLocation.name,
                    address: newLocation.address
                }
            });
            setIsCreateDialogOpen(false);
            setNewLocation({ name: '', address: '' });
            showToast(`Location created successfully`, 'success');
        } catch (error) {
            showToast(`Failed to create location`, 'error');
        }
    };

    return (
        <div className="locations">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs/>
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>Locations <div className="divider" />
                        <Button
                            appearance="primary"
                            icon={<AddRegular />}
                            onClick={() => setIsCreateDialogOpen(true)}
                        >
                            New Location
                        </Button>
                    </h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={isLoading}
                isEmpty={locations.length === 0}
                emptyMessage={`No locations found.`}
                emptyIcon={<GlobeLocationRegular />}
            >
                <div className="emc-grid">
                    <div className="emc-grid__header">
                        <div className="emc-grid__cell checkbox">
                            <Checkbox
                                checked={getSelectionState()}
                                onChange={(_, data: CheckboxOnChangeData) => handleSelectAll(!!data.checked)}
                            />
                        </div>
                        <div className="emc-grid__cell">Location</div>
                        <div className="emc-grid__cell">Address</div>
                        <div className="emc-grid__cell actions">Actions</div>
                    </div>

                    <div className="emc-grid__body">
                        {locations.map((location) => (
                            <div key={location.id} className={editingLocation?.id === location.id ? "emc-grid__row editing" : "emc-grid__row"}>
                                <div className="emc-grid__cell checkbox">
                                    <Checkbox
                                        checked={selectedLocations.includes(location.id!)}
                                        onChange={(_, data: CheckboxOnChangeData) => handleSelect(location.id!, !!data.checked)}
                                    />
                                </div>
                                <div className="emc-grid__cell">
                                    {editingLocation?.id === location.id ? (
                                        <Input
                                            required
                                            className="emc-grid__input"
                                            appearance="underline"
                                            value={editedValues.name || ''}
                                            onChange={(_, data) => setEditedValues(prev => ({ ...prev, name: data.value }))}
                                        />
                                    ) : location.name}
                                </div>
                                <div className="emc-grid__cell">
                                    {editingLocation?.id === location.id ? (
                                        <Input
                                            required
                                            className="emc-grid__input"
                                            appearance="underline"
                                            value={editedValues.address || ''}
                                            onChange={(_, data) => setEditedValues(prev => ({ ...prev, address: data.value }))}
                                        />
                                    ) : location.address}
                                </div>
                                <div className="emc-grid__cell actions">
                                    {editingLocation?.id === location.id ? (
                                        <>
                                            <Button
                                                appearance="primary"
                                                icon={<CheckmarkRegular />}
                                                onClick={() => handleEditConfirm()}
                                                disabled={updateLocationMutation.isPending}
                                            />
                                            <Button
                                                appearance="secondary"
                                                icon={<DismissRegular />}
                                                onClick={() => setEditingLocation(null)}
                                                disabled={updateLocationMutation.isPending}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                appearance="subtle"
                                                icon={<EditRegular />}
                                                onClick={() => handleEdit(location)}
                                                disabled={updateLocationMutation.isPending}
                                            />
                                            <Button
                                                appearance="subtle"
                                                icon={<DeleteRegular />}
                                                onClick={() => handleDeleteClick(location)}
                                                disabled={deleteLocationMutation.isPending}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <Paginator
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={setItemsPerPage}
                    totalItems={totalCount}
                    currentItems={(currentPage - 1) * itemsPerPage + 1}
                    showItemsPerPage={true}
                />
            </LoadingWrapper>

            {selectedLocations.length > 0 && (
                <div className="emc-page-footer">
                    <Checkbox
                        checked={getSelectionState()}
                        onChange={(_, data) => handleSelectAll(!!data.checked)}
                        label="Select All"
                    />
                    <Button
                        appearance="secondary"
                        onClick={() => setSelectedLocations([])}
                    >
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<DeleteRegular />}
                        onClick={handleDeleteMultipleClick}
                    >
                        Delete Selected
                    </Button>
                </div>
            )}

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title={`Delete ${locationsToDelete.length > 1 ? 'Locations' : 'Location'}`}
                primaryButtonText="Delete"
                onPrimaryButtonClick={handleConfirmDelete}
                isPrimaryButtonLoading={deleteLocationMutation.isPending}
            >
                Permanently delete {locationsToDelete.length === 1 ? `location ${locationsToDelete[0]?.name}` : `${locationsToDelete.length} locations`}?
                You can't undo this action.
            </DialogBox>

            <DialogBox
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
                title="Add Location"
                primaryButtonText="Add Location"
                onPrimaryButtonClick={handleConfirmCreate}
                isPrimaryButtonDisabled={!newLocation.name.trim()}
                isPrimaryButtonLoading={createLocationMutation.isPending}
            >
                <Field label="Location Name" required>
                    <Input
                        value={newLocation.name}
                        placeholder="Please enter location name"
                        onChange={(e, data) => setNewLocation({ ...newLocation, name: data.value })}
                    />
                </Field>
                <Field label="Address">
                    <Input
                        value={newLocation.address}
                        placeholder="Please enter location address"
                        onChange={(e, data) => setNewLocation({ ...newLocation, address: data.value })}
                    />
                </Field>
            </DialogBox>
        </div>
    );
};

export default Locations;
